<template>
  <header class="app-header">
    <router-link to="/" class="app-header__brand" @click="closeMobileMenu">
      {{ $t('footer.brand.name') }}
    </router-link>

    <nav class="app-header__desktop-nav" aria-label="Main navigation">
      <RouterLink
        v-for="item in navItems"
        :key="item.to"
        :to="item.to"
        class="nav-link"
      >
        <span>{{ $t(item.labelKey) }}</span>
      </RouterLink>
    </nav>

    <div class="app-header__actions">
      <LanguageSwitcher class="app-header__language" />
      <button
        class="app-header__menu-button"
        type="button"
        :aria-expanded="isMobileMenuOpen ? 'true' : 'false'"
        :aria-label="isMobileMenuOpen ? $t('common.close') : $t('common.more')"
        @click="toggleMobileMenu"
      >
        <i :class="['fas', isMobileMenuOpen ? 'fa-times' : 'fa-bars']"></i>
      </button>
    </div>

    <transition name="mobile-nav">
      <nav
        v-show="isMobileMenuOpen"
        class="app-header__mobile-nav"
        aria-label="Mobile navigation"
      >
        <RouterLink
          v-for="item in navItems"
          :key="`mobile-${item.to}`"
          :to="item.to"
          class="nav-link"
          @click="closeMobileMenu"
        >
          <span>{{ $t(item.labelKey) }}</span>
        </RouterLink>
      </nav>
    </transition>
  </header>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import LanguageSwitcher from './LanguageSwitcher.vue';

const route = useRoute();
const isMobileMenuOpen = ref(false);

const navItems = [
  { to: '/', labelKey: 'nav.home' },
  { to: '/service', labelKey: 'nav.coreService' },
  { to: '/product', labelKey: 'nav.industrySolutions' },
  { to: '/case-studies', labelKey: 'nav.caseStudies' },
  { to: '/resource-center', labelKey: 'nav.resourceCenter' },
  { to: '/customer-support', labelKey: 'nav.customerSupport' },
  { to: '/about', labelKey: 'nav.about' },
  { to: '/contact', labelKey: 'nav.contact' }
];

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false;
};

watch(() => route.fullPath, closeMobileMenu);
</script>

<style scoped>
@import '@/assets/css/main.css';

.app-header {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr) max-content;
  align-items: center;
  width: 100%;
  min-height: 4.5rem;
  gap: clamp(0.75rem, 1.4vw, 1.5rem);
  padding: 0.9rem clamp(1.25rem, 3vw, 3rem);
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.09);
  backdrop-filter: blur(14px);
}

.app-header__brand {
  color: #7c3aed;
  font-size: clamp(1.2rem, 1.6vw, 1.5rem);
  font-weight: 800;
  line-height: 1;
  white-space: nowrap;
  text-decoration: none;
  transition: color 0.3s ease;
}

.app-header__brand:hover {
  color: #6d28d9;
}

.app-header__desktop-nav {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  gap: clamp(0.12rem, 0.35vw, 0.5rem);
}

.app-header__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 0;
  gap: 0.5rem;
}

.app-header__menu-button {
  display: none;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  flex: 0 0 auto;
  border: 0;
  border-radius: 9999px;
  color: #374151;
  background: #f8fafc;
  box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.3);
  cursor: pointer;
  transition: all 0.25s ease;
}

.app-header__menu-button:hover {
  color: #7c3aed;
  background: #f3e8ff;
}

.app-header__mobile-nav {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.55rem;
  padding: 0.85rem 1rem 1rem;
  background: rgba(255, 255, 255, 0.99);
  box-shadow: 0 18px 36px rgba(15, 23, 42, 0.12);
  border-top: 1px solid rgba(226, 232, 240, 0.9);
}

/* 导航链接基础样式 */
.nav-link {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  padding: 0.5rem clamp(0.58rem, 0.85vw, 1rem);
  border-radius: 0.5rem;
  text-align: center;
  color: #374151; /* text-gray-700 */
  font-size: clamp(0.9rem, 0.9vw, 1rem);
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  text-decoration: none;
  transition: all 0.3s ease;
}

.nav-link:hover {
  color: #7c3aed; /* text-purple-700 */
  background-color: #f3e8ff; /* bg-purple-100 */
}

/* 选中状态样式 - 紫色背景 */
.nav-link.router-link-active,
.nav-link.router-link-exact-active {
  color: #ffffff;
  background: linear-gradient(to right, #6A11CB, #8E2DE2);
  box-shadow: 0 4px 12px rgba(106, 17, 203, 0.3);
}

.nav-link.router-link-active:hover,
.nav-link.router-link-exact-active:hover {
  background: linear-gradient(to right, #5a0fb5, #7e1dcf);
  color: #ffffff;
}

.mobile-nav-enter-active,
.mobile-nav-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.mobile-nav-enter-from,
.mobile-nav-leave-to {
  opacity: 0;
  transform: translateY(-0.35rem);
}

@media (max-width: 1279.98px) {
  .app-header {
    grid-template-columns: minmax(0, max-content) max-content;
    justify-content: space-between;
    padding: 0.8rem clamp(1rem, 3.2vw, 1.75rem);
  }

  .app-header__desktop-nav {
    display: none;
  }

  .app-header__menu-button {
    display: inline-flex;
  }

  .nav-link {
    min-height: 2.75rem;
  }
}

@media (max-width: 640px) {
  .app-header {
    min-height: 4.25rem;
    padding-inline: 0.9rem;
  }

  .app-header__brand {
    font-size: 1.15rem;
    max-width: min(48vw, 12rem);
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .app-header__mobile-nav {
    grid-template-columns: 1fr;
  }
}
</style>
